import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import Popup, { ToolbarItem, Position } from 'devextreme-react/popup';
import List from 'devextreme-react/list';

import isEqual from 'lodash/isEqual';
function areEqual(prevProps, nextProps) {
  return isEqual(prevProps, nextProps);
}

const CLOSE = 'close';

const CustomColumnChooser = ({
  container,
  visible,
  columns,
  onHiding,
  onChangesApplied,
  gridRef,
}) => {
  const gridInstance = gridRef?.current?.instance;
  // was copied from devextreme columnChooser sources
  const rowsView = gridInstance?._views?.rowsView;

  function getGridColumns() {
    return gridInstance?.option('columns');
  }

  function getDataSource() {
    return gridInstance?.getDataSource();
  }

  function setColumnOptions(id, options) {
    gridInstance.columnOption(id, options);
  }

  const visibleColumns = React.useMemo(() => {
    return getVisibleColumnNames();
  }, [columns, gridInstance])

  function getVisibleColumnNames(data = columns) {
    if (!gridInstance) {
      return data
        .filter(column => column.props.visible)
        .map(column => column.props.caption);
    }

    const visibleColumns = [];
    const columnCount = getGridColumns().length;
    for (let i = 0; i < columnCount; i++) {
      const column = gridInstance.columnOption(i);
      column.visible && column.caption && visibleColumns.push(column.caption);
    }

    return visibleColumns;
  }

  const listRef = useRef(null);
  const [selectedItems, setSelectedItems] = useState(visibleColumns);
  const [addedItems, setAddedItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const appSettings = useSelector(reduxStore => reduxStore.appState.appSettings);

  useEffect(() => {
    setSelectedItems(visibleColumns);
  }, [columns, setSelectedItems, gridRef]);

  const columnsList = useMemo(() => {
    return columns
      .filter(column => !column.props.fixed)
      .map(column => column.props.caption)
      .sort((col1, col2) => col1.localeCompare(col2));
  }, [columns]);

  const tooltips = useMemo(() => {
    const tooltipsObj = {};
    appSettings.FieldSettings.forEach(field => {
      tooltipsObj[field.displayName] = field.tooltip ?? field.displayName;
    });

    return tooltipsObj;
  }, [appSettings]);

  function onSelectionChanged(e) {
    const selectedItems = e.component.option('selectedItems');
    setSelectedItems(selectedItems);

    // select all case
    if (selectedItems.length === columnsList.length) {
      setAddedItems(columnsList);
      setRemovedItems([]);
      return;
    }

    // deselect all case
    if (e.removedItems.length === columnsList.length) {
      setAddedItems([]);
      setRemovedItems(e.removedItems);
      return;
    }

    // one item was added case
    if (e.addedItems.length) {
      const newAddedItems = [...addedItems, ...e.addedItems];
      setAddedItems(newAddedItems);
      removedItems.includes(e.addedItems[0])
      && setRemovedItems([...removedItems].filter(item => item !== e.addedItems[0]));
    }

    // one item was removed case
    if (e.removedItems.length) {
      const newRemovedItems = [...removedItems, ...e.removedItems];
      setRemovedItems(newRemovedItems);
      addedItems.includes(e.removedItems[0])
      && setAddedItems([...addedItems].filter(item => item !== e.removedItems[0]));
    }
  }

  function withSelect(gridInstance) {
    const newSelect = [];
    const columnCount = getGridColumns().length;

    gridInstance.beginUpdate();

    for (let i = 0; i < columnCount; i++) {
      const column = gridInstance.columnOption(i);
      column.visible
      && !removedItems.includes(column.caption)
      && newSelect.push(column.dataField);

      !column.visible
      && addedItems.includes(column.caption)
      && newSelect.push(column.dataField);

      if (removedItems.includes(column.caption)) {
        setColumnOptions(column.dataField, { visible: false });
      }

      if (addedItems.includes(column.caption)) {
        const options = { visible: true };
        const defaultIndex = appSettings.FieldIndex.channelWorkflowGrid[column.dataField];
        if (defaultIndex != null) {
          options.visibleIndex = defaultIndex;
        }
        setColumnOptions(column.dataField, options);
      }
    }

    gridInstance.endUpdate();

    return newSelect;
  }

  function withoutSelect(gridInstance) {
    if (removedItems.length || addedItems.length) {
      const columnCount = gridInstance.option('columns').length;

      gridInstance.beginUpdate();
      for (let i = 0; i < columnCount; i++) {
        const column = gridInstance.columnOption(i);

        if (removedItems.includes(column.caption)) {
          setColumnOptions(column.dataField, { visible: false });
        }

        if (addedItems.includes(column.caption)) {
          setColumnOptions(column.dataField, { visible: true });
        }
      }
      gridInstance.endUpdate();
    }
  }

  function onApply() {
    if (container === '#workflowGrid') {
      const select = withSelect(gridInstance);

      if (addedItems.length) {
        const src = getDataSource();
        src.select(select);
        src.reload();
        onChangesApplied && onChangesApplied();
      }
    } else {
      withoutSelect(gridInstance);

      if (addedItems.length) {
        const src = getDataSource();
        src.reload();
        onChangesApplied && onChangesApplied();
      }
    }
    setAddedItems([]);
    setRemovedItems([]);
  }

  function hideHandler() {
    onHiding();
  }

  function applyHandler() {
    onApply();
  }

  function applyAndCloseHandler() {
    onApply();
    // hide column chooser
    hideHandler();
  }

  function onCancel() {
    setSelectedItems(getVisibleColumnNames());
    setAddedItems([]);
    setRemovedItems([]);
    hideHandler();
  }

  function addTooltip(args) {
    args.itemElement.setAttribute('title', tooltips[args.itemData] || args.itemData);
  }

  return (
    <>
      { visible &&
        <Popup
          container="body"
          title="Column Chooser"
          defaultWidth={333}
          defaultHeight={640}
          minWidth={333}
          minHeight={320}
          resizeEnabled={true}
          shading={false}
          showCloseButton={false}
          dragEnabled={true}
          hideOnOutsideClick={false}
          visible={visible}
        >
          <Position
            my="right bottom"
            at="right bottom"
            of={ rowsView && rowsView.element() }
            collision="fit"
            offset="-2 -2"
            boundaryOffset="2 2"
          />
          <List
            ref={listRef}
            dataSource={columnsList}
            selectionMode="all"
            showSelectionControls
            selectedItems={selectedItems}
            onSelectionChanged={onSelectionChanged}
            searchEnabled
            searchMode="contains"
            selectAllMode="allPages"
            pageLoadMode="scrollBottom"
            onItemRendered={addTooltip}
          />
          <ToolbarItem
            widget="dxButton"
            location="after"
            toolbar="bottom"
            options={{ text: 'Apply', onClick: applyHandler }}
          />
          <ToolbarItem
            widget="dxButton"
            location="after"
            toolbar="bottom"
            options={{ text: 'Apply & Close', onClick: applyAndCloseHandler }}
          />
          <ToolbarItem
            widget="dxButton"
            location="after"
            toolbar="bottom"
            options={{ text: 'Cancel', onClick: onCancel }}
          />
          <ToolbarItem
            widget="dxButton"
            location="after"
            toolbar="top"
            options={{ type: CLOSE, icon: CLOSE, onClick: onCancel }}
          />
        </Popup>
      }
    </>
  )
}

export default React.memo(CustomColumnChooser, areEqual);
